import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ChevronRight, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { createTask } from "@/lib/time-api";
import { TaskFormDialog } from "@/components/time/TaskFormDialog";
import type { Task } from "@/types/time";

interface TaskListProps {
  projectId: string;
  tasks: Task[];
  isAdmin: boolean;
  onMutate: () => void;
}

export function TaskList({ projectId, tasks, isAdmin, onMutate }: TaskListProps) {
  const { orgId, slug } = useParams<{ orgId: string; slug: string }>();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  const activeTasks = tasks.filter((t) => !t.isArchived);
  const base = `/${orgId}/${slug}/settings/projects/${projectId}/tasks`;

  async function handleCreate(data: { name: string }) {
    try {
      await createTask(projectId, data);
      toast.success("Task added");
      onMutate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to add task");
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Tasks</h3>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add
          </Button>
        )}
      </div>

      {activeTasks.length > 0 ? (
        <div className="divide-y">
          {activeTasks.map((task) => (
            <div
              key={task.id}
              className="flex items-center justify-between py-2 cursor-pointer hover:bg-muted/50 -mx-2 px-2 rounded-md transition-colors"
              onClick={() => navigate(`${base}/${task.id}`)}
            >
              <div className="flex items-center gap-2.5">
                <Zap className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm">{task.name}</span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No tasks yet.</p>
      )}

      <TaskFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
      />
    </div>
  );
}
